import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWeightScale } from '@fortawesome/free-solid-svg-icons';
import { Helmet } from 'react-helmet-async';
import ChatbotAi from "../components/ChatbotAi";
import '../css/calculatorpages.css';

const BMICalculator = () => {
    const [gender, setGender] = useState('male');
    const [height, setHeight] = useState('');
    const [weight, setWeight] = useState('');
    const [age, setAge] = useState('');

    const [bmi, setBmi] = useState(null);
    const [bmiCategory, setBmiCategory] = useState('');
    const [healthyWeightRange, setHealthyWeightRange] = useState({ min: 0, max: 0 });
    const [actionRequired, setActionRequired] = useState(null);

    const navigate = useNavigate();

    const handleBackClick = () => {
        navigate('/#financial-calculators-section');
    };

    const calculateBMI = () => {
        const hInMeters = parseFloat(height) / 100;
        const w = parseFloat(weight);

        if (hInMeters > 0 && w > 0) {
            const calculatedBmi = w / (hInMeters * hInMeters);
            setBmi(calculatedBmi.toFixed(2));

            let category = '';
            if (calculatedBmi < 18.5) {
                category = 'Under Weight';
            } else if (calculatedBmi >= 18.5 && calculatedBmi < 24.9) {
                category = 'Normal';
            } else if (calculatedBmi >= 25 && calculatedBmi < 29.9) {
                category = 'Over Weight';
            } else {
                category = 'Obesity';
            }
            setBmiCategory(category);

            const minHealthyWeight = 18.5 * (hInMeters * hInMeters);
            const maxHealthyWeight = 24.9 * (hInMeters * hInMeters);
            setHealthyWeightRange({ min: minHealthyWeight.toFixed(1), max: maxHealthyWeight.toFixed(1) });

            let action = null;
            if (calculatedBmi < 18.5) {
                action = `Gain ${(minHealthyWeight - w).toFixed(1)} Kg`;
            } else if (calculatedBmi >= 25) {
                action = `Lose ${(w - maxHealthyWeight).toFixed(1)} Kg`;
            }
            setActionRequired(action);

        } else {
            setBmi(null);
            setBmiCategory('');
            setHealthyWeightRange({ min: 0, max: 0 });
            setActionRequired(null);
        }
    };

    return (
        <div>
            <Helmet>
                <title>BMI Calculator | Check Your Body Mass Index</title>
                <meta name="description" content="Calculate your Body Mass Index easily using our accurate BMI calculator. Understand your health status and make smarter lifestyle choices." />
                <link rel="canonical" href="https://policysamjho.com/calculators/bmi-calculator/" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "SoftwareApplication",
                        "name": "BMI Calculator",
                        "description": "Calculate your Body Mass Index easily using our accurate BMI calculator. Understand your health status and make smarter lifestyle choices.",
                        "url": "https://policysamjho.com/calculators/bmi-calculator/",
                        "applicationCategory": "HealthApplication",
                        "operatingSystem": "Web",
                        "offers": {
                            "@type": "Offer",
                            "price": "0",
                            "priceCurrency": "INR"
                        },
                        "provider": {
                            "@type": "Organization",
                            "name": "Policy Samjho",
                            "url": "https://policysamjho.com/"
                        }
                    })}
                </script>
            </Helmet>

            <div className="calculator-page-container">
                <button onClick={handleBackClick} className="calculator-back-button">
                    Back
                </button>
                <div className="calculator-content-wrapper">
                    <div className="calculator-info-section">
                        <div className="calculator-header-content">
                            <FontAwesomeIcon icon={faWeightScale} className="calculator-header-icon" />
                            <h1 className="calculator-title">BMI Calculator</h1>
                        </div>
                        <p className="calculator-description">Health should be a top priority for an individual. A sound mind and body is needed to enjoy the beautiful things around us. While exercising and a healthy nutritious diet is the first step towards achieving a healthy body, regularly tracking your body weight is equally crucial. Tools such as BMI Calculator allow you to determine an ideal body weight corresponding to your height and age.
                            Let's know more about BMI calculator, how it works, BMI ranges, importance of BMI calculator in health & life insurance, and how to maintain an optimum BMI level.</p>
                    </div>
                    <div className={`calculator-main-section ${bmi === null ? 'no-result' : ''}`}>
                        <div className="calculator-input-section">
                            <div className="input-row-group">
                                <div className="input-group">
                                    <label>Gender</label>
                                    <div className="radio-group radio-input-wrapper" style={{ gap: '40px' }}>
                                        <label>
                                            <input
                                                type="radio"
                                                name="gender"
                                                checked={gender === 'male'}
                                                onChange={() => setGender('male')}
                                            /> Male
                                        </label>
                                        <label>
                                            <input
                                                type="radio"
                                                name="gender"
                                                checked={gender === 'female'}
                                                onChange={() => setGender('female')}
                                            /> Female
                                        </label>
                                    </div>
                                </div>
                                <div className="input-group">
                                    <label htmlFor="height">Height (CM)</label>
                                    <input
                                        type="number"
                                        id="height"
                                        value={height}
                                        onChange={(e) => setHeight(e.target.value)}
                                        placeholder="e.g., 170"
                                    />
                                </div>
                            </div>

                            <div className="input-row-group">
                                <div className="input-group">
                                    <label htmlFor="weight">Weight (KG)</label>
                                    <input
                                        type="number"
                                        id="weight"
                                        value={weight}
                                        onChange={(e) => setWeight(e.target.value)}
                                        placeholder="e.g., 70"
                                    />
                                </div>
                                <div className="input-group">
                                    <label htmlFor="age">Age</label>
                                    <input
                                        type="number"
                                        id="age"
                                        value={age}
                                        onChange={(e) => setAge(e.target.value)}
                                        placeholder="e.g., 30"
                                    />
                                </div>
                            </div>

                            <button onClick={calculateBMI} className="calculate-button">Calculate</button>
                        </div>
                        {bmi !== null && (
                            <div className="calculator-result-section">
                                <h2>BMI Result</h2>
                                <div className="bmi-meter">
                                    <div className="bmi-range under"></div>
                                    <div className="bmi-range normal"></div>
                                    <div className="bmi-range over"></div>
                                    <div className="bmi-range obese"></div>
                                    <div className="bmi-pointer" style={{ left: `${Math.min(100, Math.max(0, (bmi / 50) * 100))}%` }}></div>
                                </div>

                                <div className="result-item">
                                    <span>Your BMI</span>
                                    <span className="result-value">{bmi} ({bmiCategory})</span>
                                </div>
                                <div className="result-item">
                                    <span>Healthy BMI range</span>
                                    <span className="result-value">18.5 - 24.9</span>
                                </div>
                                <div className="result-item">
                                    <span>Healthy weight</span>
                                    <span className="result-value">{healthyWeightRange.min} - {healthyWeightRange.max} Kg</span>
                                </div>
                                {actionRequired && (
                                    <div className="result-item result-action">
                                        <span>Action</span>
                                        <span className="result-value">{actionRequired}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <ChatbotAi />
        </div>
    );
};

export default BMICalculator;